import type { CoupleSpace, Ticket, TicketDraft, TicketType } from '../../domain/types';
import { statusLabels } from './home-view';

export type CreateViewModel =
  | { phase: 'form'; type: TicketType; space: CoupleSpace; draft?: TicketDraft; error?: string }
  | { phase: 'created'; ticket: Ticket; shareUrl: string; shareCard?: Blob; cardError?: string; partnerName: string };

export interface CreateViewActions {
  onSubmit?: (draft: TicketDraft, photo: File | null) => void | Promise<void>;
  onShare?: (url: string, card: Blob | null, status: HTMLElement) => void | Promise<void>;
}

export function renderCreateView(
  container: HTMLElement,
  model: CreateViewModel,
  actions: CreateViewActions = {},
): void {
  container.replaceChildren();
  if (model.phase === 'created') {
    renderCreated(container, model, actions);
    return;
  }

  const section = document.createElement('section');
  section.className = 'page-card stack create-view';
  section.innerHTML = `
    <div>
      <p class="eyebrow">写给 ${escapeText(model.space.partnerName)}</p>
      <h1>${model.type === 'reward' ? '创建奖励' : '创建小罚单'}</h1>
    </div>
    <div class="segmented" aria-label="单据类型">
      <a href="#create/reward" aria-current="${model.type === 'reward' ? 'true' : 'false'}">奖励</a>
      <a href="#create/penalty" aria-current="${model.type === 'penalty' ? 'true' : 'false'}">小罚单</a>
    </div>
    <form id="ticket-create-form" class="stack">
      <label class="field">标题<input name="title" maxlength="40" value="${escapeText(model.draft?.title ?? '')}" required></label>
      <label class="field">详细说明<textarea name="description" maxlength="300" rows="4">${escapeText(model.draft?.description ?? '')}</textarea></label>
      <label class="field">${model.type === 'reward' ? '奖励内容' : '惩罚内容'}<textarea name="outcome" maxlength="160" rows="3" required>${escapeText(model.draft?.outcome ?? '')}</textarea></label>
      <label class="field">截止日期<input name="dueDate" type="date" value="${escapeText(model.draft?.dueDate ?? '')}"></label>
      <label class="field">重要程度<select name="importance"><option value="small">小小心意</option><option value="promise">认真约定</option><option value="important">很重要</option></select></label>
      <label class="field">标签<input name="tags" maxlength="32" value="${escapeText(model.draft?.tags.join('，') ?? '')}" placeholder="最多 3 个，用逗号分隔"></label>
      <label class="field">照片<input name="photo" type="file" accept="image/*"><small class="muted">照片只保存在本机和分享卡片里，不会进入链接。</small></label>
      <label class="field">署名<input name="signature" maxlength="20" value="${escapeText(model.draft?.signature ?? model.space.selfName)}" required></label>
      <button class="button button--primary" type="submit">生成单据与微信链接</button>
    </form>
  `;
  const form = section.querySelector<HTMLFormElement>('#ticket-create-form')!;
  section.querySelector<HTMLSelectElement>('select[name="importance"]')!.value = model.draft?.importance ?? 'small';
  if (model.error) appendError(section, model.error, form, 'ticket-create-error');
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const data = new FormData(form);
    const photo = data.get('photo');
    void actions.onSubmit?.({
      type: model.type,
      title: formString(data, 'title'),
      description: formString(data, 'description'),
      outcome: formString(data, 'outcome'),
      dueDate: formString(data, 'dueDate') || null,
      importance: importanceValue(formString(data, 'importance')),
      tags: formString(data, 'tags').split(/[，,]/u).map((tag) => tag.trim()).filter(Boolean),
      signature: formString(data, 'signature'),
      imageId: null,
    }, photo instanceof File && photo.size > 0 ? photo : null);
  });
  container.append(section);
}

function renderCreated(
  container: HTMLElement,
  model: Extract<CreateViewModel, { phase: 'created' }>,
  actions: CreateViewActions,
): void {
  const section = document.createElement('section');
  section.className = 'page-card stack create-success';
  section.innerHTML = `
    <div class="success-mark" aria-hidden="true">✓</div>
    <h1 role="status">单据已生成</h1>
    <p><b>${escapeText(model.ticket.title)}</b> · ${statusLabels[model.ticket.state]}</p>
    <p class="next-step">下一步：复制链接并发给 ${escapeText(model.partnerName)}</p>
  `;
  appendShare(section, model.shareUrl, model.shareCard ?? null, model.cardError, actions);
  const detail = document.createElement('a');
  detail.className = 'button button--link';
  detail.href = `#ticket/${encodeURIComponent(model.ticket.id)}`;
  detail.textContent = '查看单据详情';
  section.append(detail);
  container.append(section);
}

export function appendShare(
  section: HTMLElement,
  value: string,
  card: Blob | null,
  cardError: string | undefined,
  actions: Pick<CreateViewActions, 'onShare'>,
): void {
  const label = document.createElement('label');
  label.className = 'field';
  label.append(document.createTextNode('微信链接'));
  const input = document.createElement('input');
  input.readOnly = true;
  input.value = value;
  input.dataset.testid = 'share-link';
  label.append(input);
  const status = document.createElement('p');
  status.className = 'muted';
  status.setAttribute('role', 'status');
  const button = document.createElement('button');
  button.className = 'button button--primary';
  button.type = 'button';
  button.dataset.testid = 'share-action';
  button.textContent = '分享给 TA';
  button.addEventListener('click', () => void actions.onShare?.(value, card, status));
  let download: HTMLElement;
  if (card) {
    const link = document.createElement('a');
    link.className = 'button button--link';
    link.href = URL.createObjectURL(card);
    link.download = '两小只-分享卡片.jpg';
    link.textContent = '保存分享卡片';
    download = link;
  } else {
    const unavailable = document.createElement('button');
    unavailable.className = 'button button--link';
    unavailable.type = 'button';
    unavailable.disabled = true;
    unavailable.textContent = '保存分享卡片';
    download = unavailable;
  }
  section.append(label, button, download, status);
  if (cardError) appendError(section, cardError);
}

function appendError(
  section: HTMLElement,
  message: string,
  describedElement?: HTMLElement,
  id = 'share-card-error',
): void {
  const error = document.createElement('p');
  error.id = id;
  error.className = 'error';
  error.setAttribute('role', 'alert');
  error.textContent = message;
  describedElement?.setAttribute('aria-describedby', id);
  section.append(error);
}

function formString(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === 'string' ? value : '';
}

function importanceValue(value: string): TicketDraft['importance'] {
  return value === 'promise' || value === 'important' ? value : 'small';
}

function escapeText(value: string): string {
  const node = document.createElement('span');
  node.textContent = value;
  return node.innerHTML;
}
